/**
 * SidebarMessage - Individual sidebar chat message bubble
 *
 * Purely presentational — all context-dependent values (isOwnMessage, timezone)
 * are computed once at the SidebarMessageList level and passed as props, avoiding
 * a context subscription inside every virtualized row.
 */

import React, { memo, useState, useEffect, useRef } from 'react';
import { generateFullName } from 'utils/generateFullNameUtils';
import { convertDateFromApiToUserTimezone } from 'utils/utility_functions/timezone';
import constants from 'constants/index';
import ImageComponent from 'shared/ImageComponent';
import { getTenantDetails } from 'utils/utils';

function SidebarMessage({ message, isOwnMessage, timezone, isNew }) {
  const senderName = generateFullName(message?.author_details);
  const isOptimistic = Boolean(message?._optimistic);
  const [tenantId] = getTenantDetails();

  // Detect optimistic → confirmed transition to play status fade-in animation
  const wasOptimisticRef = useRef(isOptimistic);
  const [justConfirmed, setJustConfirmed] = useState(false);

  useEffect(() => {
    if (wasOptimisticRef.current && !isOptimistic) {
      setJustConfirmed(true);
      const timer = setTimeout(() => setJustConfirmed(false), 600);
      return () => clearTimeout(timer);
    }
    wasOptimisticRef.current = isOptimistic;
  }, [isOptimistic]);

  // Direction-aware entrance animation: own messages slide from right, others from left
  const newAnimClass = isNew ? (isOwnMessage ? ' sidebar-message-new-own' : ' sidebar-message-new-other') : '';

  return (
    <div className={`d-flex pb-3 ${isOwnMessage ? 'justify-content-end' : 'justify-content-start'}${newAnimClass}`}>
      {/* Avatar with initial for incoming messages */}
      {!isOwnMessage && (
        <div className={`mt-1 ${isNew ? ' sidebar-avatar--new' : ''}`} title={senderName}>
          <ImageComponent
            filePath={
              message?.created_by_user_image
                ? message?.created_by_user_image
                : `${tenantId}/${message?.author_details?.firm_user_id}/profile_image`
            }
            fileName={message?.author_details?.first_name}
            fileSize="medium.jpg"
            avatarSize="medium"
            style={{ borderRadius: '50%' }}
          />
        </div>
      )}

      <div
        className={`p-2 rounded ${isOwnMessage ? 'blue-bg-msg text-white' : 'bg-light'}${
          isOptimistic ? ' sidebar-message-optimistic' : ''
        }`}
        style={{ maxWidth: '75%', wordBreak: 'break-word' }}
      >
        {!isOwnMessage && <div className="fw-bold F-size12 mb-1">{senderName}</div>}
        <div className="F-size14">{message?.message}</div>
        <div className={`F-size11 text-end mt-1 sidebar-msg-status${justConfirmed ? ' sidebar-msg-status-confirmed' : ''}`}>
          {isOptimistic ? (
            <span>
              <i className="pi pi-clock" style={{ fontSize: '0.7rem' }} />
              {' Sending...'}
            </span>
          ) : (
            convertDateFromApiToUserTimezone(message?.created_at || message?.timestamp, timezone, constants.month_date_year_time_12_format)
          )}
        </div>
      </div>
    </div>
  );
}

export default memo(SidebarMessage);
